import { useState } from "react";
import TheNav from "../Components/navbar";
import SEO, { breadcrumbSchema } from "../Components/SEO";

const Faq = () => {
    const [activeTab, setActiveTab] = useState("creator");
    const [openQuestions, setOpenQuestions] = useState({});

    const toggleQuestion = (index) => {
        setOpenQuestions((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const faqs = {
        creator: [
            {
                question: "Can I still make withdrawls even though I'm a creator?",
                answer: "Yes! Creators can only earn money through referrals that lead to a completed order. You will have to request a withdrawl and reply to our email."
            },
            {
                question: "How do I find editors or clippers?",
                answer:
                    "Start with the Twitch editors directory or the gaming categories page. If you want short form growth from multiple clippers, use the clipping campaigns page."
            },
            {
                question: "Can I launch a clipping campaign?",
                answer:
                    "Clipping campaigns are rolling out. You can create an account and get early access so your stream content is ready when campaigns open wider."
            },
            {
                question: "How do discounts work?",
                answer:
                    "Each dollar you spend is worth 1 point. You can redeem this points for different discount codes, which will be unique to you and one time use only. After you request, expect an email reply within 48 hours."
            },
            {
                question: "How do orders work?",
                answer:
                    "An editor sends you an offer, you accept/deny, pay via Stripe, send in your files, get a watermarked video for the delivery, and then when you accept, you get sent the unwatermarked video."
            },
            {
                question: "How do I reset my password?",
                answer: "You go to the login screen and click `Reset my password`"
            },
            {
                question: "How do I show my portfolio?",
                answer: "Go to your portfolio and connect your YouTube account. From there, your top 5 YouTube videos will be displayed for everyone to view."
            },
            {
                question: "How should I submit footage for edits or clips?",
                answer: "Send a Google Drive link, Twitch VOD link, timestamps, references, and notes about the platforms you care about."
            },
            {
                question: "How do I know my payment is safe?",
                answer: "We use Stripe, the most common, safe and secure payment system, not some random platform along with HTTPS. "
            },
            {
                question: "How do I redeem my points?",
                answer: "You can go to the redemption page and view which items you can redeem."
            },
            {
                question: "If there is an issue, who do I reach out to?",
                answer: "anteos1@kre8content.com. We will get back to you as soon as possible."
            }
        ],
        editor: [
            {
                question: "Can I join as a clipper through the editor signup?",
                answer:
                    "Yes. Use the editor signup flow, then make your profile clear that you focus on clipping, TikToks, Shorts, Reels, or stream moments."
            },
            {
                question: "How do I view available creators?",
                answer:
                    "Go to `Find Creators` and view creators with the specific game you're looking for."
            },
            {
                question: "How do I get paid?",
                answer:
                    "Withdrawls are processed through Stripe. Make sure your payment details are updated in your profile and go to your orders page."
            },
            {
                question: "How do I build my portfolio?",
                answer:
                    "Upload links to YouTube videos, TikToks, Shorts, Reels, or stream clips you've worked on in the Portfolio section under your profile settings."
            },
            {
                question: "Do I have to offer long form editing?",
                answer:
                    "No. Clippers can focus on short form clips. Editors can offer long form work, short form work, or both."
            },
            {
                question: "How can I be sure that they can't steal my video and not complete payment?",
                answer: "After you submit each draft, the video is watermarked with the Kre8 Content logo. The logo is only removed on the final delivery."
            },
            {
                question: "How do I redeem my points?",
                answer: "You can go to the redemption page and view which items you can redeem."
            },
            {
                question: "If there is an issue, who do I reach out to?",
                answer: "anteos1@kre8content.com. We will get back to you as soon as possible."
            },
            {
                question: "I live in a country that doesn't accept Stripe. How do I get paid?",
                answer: "Send an email to anteos1@kre8content.com and we will get back to you as soon as possible. We will use another payment method."
            }
        ],
        clipper: [
            {
                question: "What is a clipper on Kre8?",
                answer:
                    "A clipper focuses on finding and cutting Twitch stream moments into short form content for TikTok, Shorts, and Reels."
            },
            {
                question: "Are clipping campaigns live right now?",
                answer:
                    "Campaigns are being rolled out. Joining early gives you a profile and a place to send streamers while the campaign system opens."
            },
            {
                question: "What should I put on my profile?",
                answer:
                    "Show the games you understand, the creators or clips you've worked on, your editing style, and the short form platforms you know best."
            },
            {
                question: "Will clippers need to do normal editing?",
                answer:
                    "No. Clipping is treated as its own specialization. You can offer deeper edits if you want, but the clipper path is built around stream moments and short form growth."
            },
            {
                question: "How should I talk about campaign access?",
                answer:
                    "Keep it honest. You are joining early, building a profile, and getting ready for streamer clipping campaigns as Kre8 rolls them out."
            }
        ]
    };

    return (
        <>
            <SEO
                title="Kre8 FAQ | Twitch & Gaming Video Editing Marketplace"
                description="Answers for Twitch streamers, gaming creators, editors, and clippers using Kre8, including clipping campaigns, profiles, orders, payouts, and early access."
                canonicalPath="/faq"
                structuredData={breadcrumbSchema([
                    { name: "Home", path: "/" },
                    { name: "FAQ", path: "/faq" },
                ])}
            />
            <TheNav />
            <div className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-center text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                        Frequently Asked Questions
                    </h2>
                    <p className="text-center text-sm text-gray-500 mb-6">
                        Quick answers before you create your account.
                    </p>

                    <div className="flex justify-center mb-8 gap-4 flex-wrap">
                        <button
                            onClick={() => setActiveTab("creator")}
                            className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${activeTab === "creator"
                                ? "bg-indigo-600 text-white"
                                : "bg-gray-200 text-gray-700"
                                }`}
                        >
                            Creator
                        </button>
                        <button
                            onClick={() => setActiveTab("editor")}
                            className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${activeTab === "editor"
                                ? "bg-indigo-600 text-white"
                                : "bg-gray-200 text-gray-700"
                                }`}
                        >
                            Editor
                        </button>
                        <button
                            onClick={() => setActiveTab("clipper")}
                            className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${activeTab === "clipper"
                                ? "bg-indigo-600 text-white"
                                : "bg-gray-200 text-gray-700"
                                }`}
                        >
                            Clipper
                        </button>
                    </div>

                    <div className="space-y-4">
                        {faqs[activeTab].map((item, index) => (
                            <div
                                key={index}
                                className={`rounded-lg overflow-hidden ${openQuestions[index] ? "bg-indigo-50" : "bg-white"
                                    } shadow transition-all duration-200`}
                            >
                                <button
                                    className="w-full text-left px-4 py-4 flex justify-between items-center text-gray-800 font-medium text-base"
                                    onClick={() => toggleQuestion(index)}
                                >
                                    {item.question}
                                    <span>{openQuestions[index] ? "▲" : "▼"}</span>
                                </button>
                                {openQuestions[index] && (
                                    <div className="px-4 pb-4 text-sm text-gray-600">
                                        {item.answer}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Faq;
